/**
 * Em'z Blendz gift wrap package builder
 *
 * Pairs with snippets/emz-gift-wrap.liquid on the cart page. Customers
 * assign cart lines (whole or part of a line's quantity) to numbered
 * gift packages and write a message per package. Saving rewrites the
 * cart through the AJAX API:
 *
 *   - every wrapped item line carries the visible property
 *     "Gift package: N" (canonical for fulfillment)
 *   - every package gets one wrap line (qty 1, Paper & Ribbon variant)
 *     with properties "Package", "Contains", and "Gift message"
 *
 * Wrap lines are always removed and re-added on save so the "Contains"
 * summary regenerates. All cart mutations run one at a time through a
 * promise queue: parallel Shopify cart writes race and corrupt state.
 * Quantity reductions on original lines run before any adds, so a
 * re-add that auto-merges into an existing line can never be clobbered
 * by a later reduction taken from the stale snapshot.
 *
 * Unwrapping never trusts /cart/add.js auto-merge to reunite the
 * re-added quantity with a surviving plain line: Shopify stores
 * empty-valued and app-injected properties invisibly, and any stored
 * mismatch makes the merge silently fail, stranding the quantities on
 * separate lines. Instead a matching plain line is folded into the
 * re-add (both removed, combined quantity added back), and properties
 * with empty values are always stripped from re-adds.
 *
 * The queue must survive hostile conditions: Shopify throttles bursts
 * of cart writes (HTTP 429, and other app scripts share that budget),
 * and /cart/change.js fails 400 on a stale line key. A step that dies
 * mid-queue strands a half-rewritten cart with no wrap line but
 * properties intact. Three defenses: the model is rebuilt from a
 * fresh /cart.js AT SAVE TIME (open-time snapshots go stale while the
 * builder sits open), every step retries transient failures with
 * backoff, and change retries verify against /cart.js first so an
 * operation that actually landed is never replayed.
 *
 * Self-healing (spec Phase 3, pulled into Phase 2): customers edit the
 * cart with the theme's own controls, which know nothing about
 * packages. On /cart load and after every cart:updated event (cart
 * page and drawer edits both dispatch it), the cart is checked while
 * the builder is closed: member lines whose wrap line is gone lose
 * their "Gift package" property through the same hard-strip fold the
 * builder uses, wrap lines whose package has no members left are
 * removed with an inline notice, and stale "Contains" summaries are
 * regenerated. A consistent cart is a no-op, so the post-heal reload
 * cannot loop.
 *
 * After the queue settles, ajaxProduct:added is dispatched once (header
 * count and drawer listeners expect it) and the page reloads, the same
 * finish the theme itself uses for adds made from the cart page.
 *
 * Builder state is fetched fresh from /cart.js on every open; the
 * Liquid snippet only supplies product tags (absent from /cart.js) and
 * the wrap price. Validation and self-healing beyond MAX_PACKAGES are
 * Phase 3.
 */
(function () {
  'use strict';

  if (window.emzGiftWrapLoaded) return;
  window.emzGiftWrapLoaded = true;

  // ---------------------------------------------------------------- config

  var MAX_PACKAGES = 4;
  var GIFT_MESSAGE_MAX_CHARS = 200;
  var WRAP_PRODUCT_ID = 1701645615170;
  var WRAP_VARIANT_PAPER = 14066474451010;
  var ROPE_PRODUCT_ID = 7249236688962;

  // Line item property keys; fulfillment reads these on packing slips.
  var PROP_MEMBER = 'Gift package';
  var PROP_PACKAGE = 'Package';
  var PROP_CONTAINS = 'Contains';
  var PROP_MESSAGE = 'Gift message';
  var PROP_ROPE_FOR = 'Rope for';   // written by emz-rope-addon.js

  // A line is not wrappable when the product id matches, or when any of
  // the substrings below appears (case-insensitively) in the product
  // type or any tag. Subscriptions and gift cards are checked off the
  // cart line itself.
  var EXCLUDED_PRODUCT_IDS = [
    WRAP_PRODUCT_ID, // the wrap product itself
    7249236688962,   // Soap on a Rope (rides tied to its bar)
    1701643026498,   // legacy Nulls "Tissue and Ribbon"
    1701643681858    // legacy Nulls "Box and Ribbon"
  ];
  var EXCLUDED_TYPE_OR_TAG_SUBSTRINGS = [
    'nulls gift product',
    'b&b',
    'guest amenities',
    'hospitality',
    'buyer:hospitality',
    'gallon',
    'bulk'
  ];

  var REASON_SUBSCRIPTION = "Subscriptions ship on their own schedule and can't be wrapped";
  var REASON_GIFT_CARD = 'Gift cards are delivered by email, so there is nothing to wrap';
  var REASON_GENERAL = 'This item is not available for gift wrapping';
  var MSG_MAX_PACKAGES = 'You can build up to ' + MAX_PACKAGES + ' gift packages in one order.';
  var MSG_SAVE_FAILED = 'Something went wrong while saving your gift options. Your cart is shown as it stands now, please try again.';
  var MSG_NOTHING_ELIGIBLE = 'Nothing in your cart can be gift wrapped right now.';

  var OPEN_HASH = '#gift-wrap';

  // ---------------------------------------------------------------- state

  var root, builder, toggle, linesEl, packagesEl, errorEl, noticeEl, saveBtn, cancelBtn;
  var wrapPrice = '';
  var tagsByProduct = {};
  var lines = [];                // snapshot of assignable cart lines
  var wrapLines = [];            // snapshot of existing wrap lines
  var ropeLines = [];            // rope follower lines (see buildModel)
  var assignments = {};          // line key -> { pkg: 0|1..MAX, qty }
  var messagesByPackage = {};    // package number -> message text
  var saving = false;
  var healing = false;
  var healTimer = null;

  var HEAL_NOTICE_FLAG = 'emzGiftWrapHealNotice';

  // One-time drawer row pulse (discoverability): fired on the first
  // drawer open with a wrappable line, at most once per session.
  var PULSE_FLAG = 'emzGiftWrapPulseShown';
  var PULSE_CLASS = 'emz-gift-wrap-drawer-row--pulse';
  var pulseShownThisPage = false;

  function cartUrl() {
    return (window.theme && theme.routes && theme.routes.cart) || '/cart.js';
  }
  function cartAddUrl() {
    return (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js';
  }
  function cartChangeUrl() {
    return (window.theme && theme.routes && theme.routes.cartChange) || '/cart/change.js';
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function thumbUrl(url) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=96';
  }

  // ---------------------------------------------------------- eligibility

  function eligibility(item) {
    if (item.selling_plan_allocation) {
      return { ok: false, reason: REASON_SUBSCRIPTION };
    }
    if (item.gift_card) {
      return { ok: false, reason: REASON_GIFT_CARD };
    }
    if (EXCLUDED_PRODUCT_IDS.indexOf(item.product_id) !== -1) {
      return { ok: false, reason: REASON_GENERAL };
    }
    var haystack = [item.product_type || ''].concat(tagsByProduct[item.product_id] || []);
    for (var i = 0; i < haystack.length; i++) {
      var value = String(haystack[i]).toLowerCase();
      for (var j = 0; j < EXCLUDED_TYPE_OR_TAG_SUBSTRINGS.length; j++) {
        if (value.indexOf(EXCLUDED_TYPE_OR_TAG_SUBSTRINGS[j]) !== -1) {
          return { ok: false, reason: REASON_GENERAL };
        }
      }
    }
    return { ok: true, reason: '' };
  }

  // ---------------------------------------------------------------- model

  function buildModel(cart) {
    lines = [];
    wrapLines = [];
    ropeLines = [];
    assignments = {};

    cart.items.forEach(function (item) {
      if (item.product_id === WRAP_PRODUCT_ID) {
        wrapLines.push(item);
        var props = item.properties || {};
        var num = parseInt(props[PROP_PACKAGE], 10);
        if (num && messagesByPackage[num] === undefined) {
          messagesByPackage[num] =
            props[PROP_MESSAGE] && props[PROP_MESSAGE] !== 'None' ? props[PROP_MESSAGE] : '';
        }
        return;
      }

      if (item.product_id === ROPE_PRODUCT_ID) {
        var ropeProps = item.properties || {};
        // A rope tied to a specific bar is a follower, never an
        // assignable row: it inherits its bar's package in the save
        // plan. Bare ropes (no "Rope for") and subscription ropes
        // fall through to the excluded-row path below.
        if (!item.selling_plan_allocation && ropeProps[PROP_ROPE_FOR]) {
          ropeLines.push({
            key: item.key,
            variantId: item.variant_id,
            quantity: item.quantity,
            ropeFor: ropeProps[PROP_ROPE_FOR],
            currentPackage: parseInt(ropeProps[PROP_MEMBER], 10) || 0
          });
          return;
        }
      }

      var properties = item.properties || {};
      var elig = eligibility(item);
      var currentPackage = parseInt(properties[PROP_MEMBER], 10) || 0;
      var line = {
        key: item.key,
        variantId: item.variant_id,
        productId: item.product_id,
        title: item.product_title,
        variantTitle:
          item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '',
        image: item.image,
        quantity: item.quantity,
        properties: properties,
        eligible: elig.ok,
        reason: elig.reason,
        currentPackage: currentPackage
      };
      lines.push(line);
      assignments[line.key] = { pkg: currentPackage, qty: item.quantity };
    });
  }

  function packageNumbersInUse() {
    var seen = {};
    var numbers = [];
    lines.forEach(function (line) {
      var a = assignments[line.key];
      if (a && a.pkg > 0 && a.qty > 0 && !seen[a.pkg]) {
        seen[a.pkg] = true;
        numbers.push(a.pkg);
      }
    });
    return numbers.sort(function (a, b) { return a - b; });
  }

  function nextFreePackageNumber() {
    var used = packageNumbersInUse();
    for (var n = 1; n <= MAX_PACKAGES; n++) {
      if (used.indexOf(n) === -1) return n;
    }
    return 0;
  }

  function lineLabel(line) {
    return line.title + (line.variantTitle ? ' (' + line.variantTitle + ')' : '');
  }

  // ------------------------------------------------------ rope followers

  function ropeGroups() {
    var byBar = {};
    var groups = [];
    ropeLines.forEach(function (line) {
      if (!byBar[line.ropeFor]) {
        byBar[line.ropeFor] = { ropeFor: line.ropeFor, variantId: line.variantId, lines: [], total: 0 };
        groups.push(byBar[line.ropeFor]);
      }
      byBar[line.ropeFor].lines.push(line);
      byBar[line.ropeFor].total += line.quantity;
    });
    return groups;
  }

  // Desired distribution of a rope group across packages, derived
  // from the matching bar's assignments: wrapped bars claim ropes
  // first, package number ascending, up to min(wrapped bar units,
  // rope units); the remainder stays plain. Rope quantity is never
  // invented, and it is TRIMMED to the bar's total units: a rope
  // whose bar left the cart is unfulfillable (the packing slip would
  // order a knot onto a bar that is not in the order), so zero bar
  // units means an empty plan and the group is removed outright.
  function ropePlanForGroup(group) {
    var totalBarUnits = 0;
    var barUnitsByPkg = {};
    lines.forEach(function (line) {
      if (line.title !== group.ropeFor) return;
      totalBarUnits += line.quantity;
      if (!line.eligible) return;
      var a = assignments[line.key];
      if (!a || a.pkg <= 0) return;
      barUnitsByPkg[a.pkg] = (barUnitsByPkg[a.pkg] || 0) + Math.max(1, Math.min(a.qty, line.quantity));
    });
    var remaining = Math.min(group.total, totalBarUnits);
    var desired = {};
    Object.keys(barUnitsByPkg)
      .map(Number)
      .sort(function (a, b) { return a - b; })
      .forEach(function (pkg) {
        var claim = Math.min(remaining, barUnitsByPkg[pkg]);
        if (claim > 0) {
          desired[String(pkg)] = claim;
          remaining -= claim;
        }
      });
    if (remaining > 0) desired['0'] = remaining;
    return desired;
  }

  // True when a group's cart lines already match the plan, so no-op
  // saves stage no mutations and self-healing converges. Duplicate
  // lines for one package count as a mismatch so they collapse.
  function ropeStateMatchesPlan(group, desired) {
    var current = {};
    var duplicate = false;
    group.lines.forEach(function (line) {
      var k = String(line.currentPackage || 0);
      if (current[k] !== undefined) duplicate = true;
      current[k] = (current[k] || 0) + line.quantity;
    });
    if (duplicate) return false;
    var desiredKeys = Object.keys(desired);
    if (desiredKeys.length !== Object.keys(current).length) return false;
    for (var i = 0; i < desiredKeys.length; i++) {
      if (current[desiredKeys[i]] !== desired[desiredKeys[i]]) return false;
    }
    return true;
  }

  function containsSummary(pkg) {
    var counts = {};
    var order = [];
    lines.forEach(function (line) {
      var a = assignments[line.key];
      if (!a || a.pkg !== pkg || a.qty < 1) return;
      var label = lineLabel(line);
      if (counts[label] === undefined) {
        counts[label] = 0;
        order.push(label);
      }
      counts[label] += a.qty;
    });
    var parts = order.map(function (label) { return counts[label] + 'x ' + label; });
    // Ropes claimed into this package ride on their bar's entry:
    // "1x Bar + rope", or "+ 2x rope" when fewer ropes than bars.
    ropeGroups().forEach(function (group) {
      var claimed = ropePlanForGroup(group)[String(pkg)] || 0;
      if (!claimed) return;
      for (var i = 0; i < order.length; i++) {
        if (order[i].indexOf(group.ropeFor) === 0) {
          parts[i] += claimed === counts[order[i]] ? ' + rope' : ' + ' + claimed + 'x rope';
          break;
        }
      }
    });
    return parts.join(', ');
  }

  // --------------------------------------------------------------- render

  function render() {
    renderLines();
    renderPackages();
  }

  function renderLines() {
    if (!lines.length) {
      linesEl.innerHTML = '';
      return;
    }

    var eligible = lines.filter(function (l) { return l.eligible; });
    var used = packageNumbersInUse();
    var canAddNew = used.length < MAX_PACKAGES;
    var ropeTitles = {};
    ropeLines.forEach(function (l) { ropeTitles[l.ropeFor] = true; });
    var html = '';

    if (!eligible.length) {
      html += '<p class="emz-gift-wrap__none">' + escapeHtml(MSG_NOTHING_ELIGIBLE) + '</p>';
    }

    lines.forEach(function (line, index) {
      var a = assignments[line.key];
      var rowClass = 'emz-gift-wrap__line' + (line.eligible ? '' : ' emz-gift-wrap__line--excluded');
      html += '<div class="' + rowClass + '" data-gw-line data-key="' + escapeHtml(line.key) + '">';

      if (line.image) {
        html +=
          '<img class="emz-gift-wrap__line-thumb" src="' + escapeHtml(thumbUrl(line.image)) +
          '" width="48" height="48" loading="lazy" alt="">';
      } else {
        html += '<span class="emz-gift-wrap__line-thumb emz-gift-wrap__line-thumb--empty"></span>';
      }

      html += '<div class="emz-gift-wrap__line-info">';
      html += '<div class="emz-gift-wrap__line-title">' + escapeHtml(line.title) + '</div>';
      var metaBits = [];
      if (line.variantTitle) metaBits.push(line.variantTitle);
      metaBits.push('Qty ' + line.quantity);
      html += '<div class="emz-gift-wrap__line-meta">' + escapeHtml(metaBits.join(' / ')) + '</div>';
      if (ropeTitles[line.title]) {
        html += '<div class="emz-gift-wrap__line-rope">+ rope, follows this bar</div>';
      }
      if (!line.eligible) {
        html += '<div class="emz-gift-wrap__line-reason">' + escapeHtml(line.reason) + '</div>';
      }
      html += '</div>';

      if (line.eligible) {
        var selectId = 'GiftWrapSelect-' + index;
        html += '<div class="emz-gift-wrap__line-controls">';
        html +=
          '<label class="hidden-label" for="' + selectId + '">Gift wrap for ' +
          escapeHtml(lineLabel(line)) + '</label>';
        html += '<select id="' + selectId + '" class="emz-gift-wrap__select" data-gw-select>';
        html += '<option value="0"' + (a.pkg === 0 ? ' selected' : '') + '>No gift wrap</option>';
        used.forEach(function (n) {
          html +=
            '<option value="' + n + '"' + (a.pkg === n ? ' selected' : '') + '>Package ' + n +
            '</option>';
        });
        if (a.pkg > 0 && used.indexOf(a.pkg) === -1) {
          html += '<option value="' + a.pkg + '" selected>Package ' + a.pkg + '</option>';
        }
        if (canAddNew) {
          html += '<option value="new">+ New package</option>';
        }
        html += '</select>';

        if (a.pkg > 0 && line.quantity > 1) {
          var qtyId = 'GiftWrapQty-' + index;
          html += '<label class="emz-gift-wrap__qty-label" for="' + qtyId + '">Wrap ';
          html +=
            '<input id="' + qtyId + '" class="emz-gift-wrap__qty" data-gw-qty type="number" min="1" max="' +
            line.quantity + '" step="1" inputmode="numeric" value="' + a.qty + '">';
          html += ' of ' + line.quantity + '</label>';
        }
        html += '</div>';
      }

      html += '</div>';
    });

    linesEl.innerHTML = html;
  }

  function renderPackages() {
    var used = packageNumbersInUse();
    var html = '';

    used.forEach(function (n) {
      var message = messagesByPackage[n] || '';
      var areaId = 'GiftWrapMessage-' + n;
      html += '<div class="emz-gift-wrap__package" data-gw-package="' + n + '">';
      html +=
        '<div class="emz-gift-wrap__package-head"><span>Package ' + n + '</span><span>' +
        escapeHtml(wrapPrice) + '</span></div>';
      html +=
        '<div class="emz-gift-wrap__package-contents">' + escapeHtml(containsSummary(n)) +
        '</div>';
      html +=
        '<label class="emz-gift-wrap__message-label" for="' + areaId +
        '">Gift message (optional)</label>';
      html +=
        '<textarea id="' + areaId + '" class="emz-gift-wrap__message" data-gw-message data-package="' +
        n + '" maxlength="' + GIFT_MESSAGE_MAX_CHARS +
        '" rows="2" placeholder="We write this on the card">' + escapeHtml(message) +
        '</textarea>';
      html +=
        '<div class="emz-gift-wrap__message-hint">Up to ' + GIFT_MESSAGE_MAX_CHARS +
        ' characters.</div>';
      html += '</div>';
    });

    packagesEl.innerHTML = html;
  }

  function showError(message) {
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function clearError() {
    errorEl.textContent = '';
    errorEl.hidden = true;
  }

  // --------------------------------------------------------- open / close

  function openBuilder() {
    clearError();
    toggle.setAttribute('aria-expanded', 'true');
    builder.hidden = false;

    fetchCart()
      .then(function (cart) {
        buildModel(cart);
        render();
      })
      .catch(function (err) {
        console.warn('[EMZ Gift Wrap] Could not load the cart', err);
        showError(MSG_SAVE_FAILED);
      });
  }

  function closeBuilder() {
    toggle.setAttribute('aria-expanded', 'false');
    builder.hidden = true;
  }

  // ----------------------------------------------------------------- save

  function propsWithout(properties, dropKey) {
    var out = {};
    var keys = Object.keys(properties || {});
    keys.forEach(function (key) {
      if (key === dropKey) return;
      var value = properties[key];
      // Empty-valued properties are stored invisibly by Shopify and
      // defeat /cart/add.js line merging; never carry them forward.
      if (value === null || value === undefined || String(value) === '') return;
      out[key] = value;
    });
    return out;
  }

  function propsEqual(a, b) {
    var keysA = Object.keys(a);
    var keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    for (var i = 0; i < keysA.length; i++) {
      var key = keysA[i];
      if (!(key in b) || String(a[key]) !== String(b[key])) return false;
    }
    return true;
  }

  function postJson(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok || (data && data.status)) {
          var error = new Error(
            (data && (data.description || data.message)) || 'Cart request failed'
          );
          error.httpStatus = response.status;
          throw error;
        }
        return data;
      });
    });
  }

  function fetchCart() {
    return fetch(cartUrl(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) { return response.json(); });
  }

  function changeLine(key, quantity) {
    return postJson(cartChangeUrl(), { id: key, quantity: quantity });
  }

  function addLine(variantId, quantity, properties) {
    var payload = { id: variantId, quantity: quantity };
    if (properties && Object.keys(properties).length) payload.properties = properties;
    return postJson(cartAddUrl(), payload);
  }

  var STEP_RETRIES = 3;

  function delay(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  // No httpStatus means the request itself died (network); 429 is the
  // cart write throttle; 5xx is Shopify's side. Anything else (400
  // stale key, 422 rejection) is a real answer and must not be
  // replayed blindly.
  function isTransient(err) {
    return !err.httpStatus || err.httpStatus === 429 || err.httpStatus >= 500;
  }

  // Sets a line's quantity, surviving throttling and dropped
  // responses. Every retry first asks /cart.js whether the change
  // already landed (or the line is already gone, for removals), so an
  // operation is never applied twice and a stale-key removal resolves
  // instead of killing the queue.
  function changeLineReliably(key, quantity) {
    var attempts = 0;
    function landed() {
      return fetchCart().then(function (cart) {
        for (var i = 0; i < cart.items.length; i++) {
          if (cart.items[i].key === key) return cart.items[i].quantity === quantity;
        }
        return quantity === 0;
      }).catch(function () { return false; });
    }
    function attempt() {
      attempts += 1;
      return changeLine(key, quantity).catch(function (err) {
        console.warn('[EMZ Gift Wrap] change attempt ' + attempts + ' failed', key, err);
        return delay(350 * attempts).then(landed).then(function (done) {
          if (done) return null;
          if (attempts >= STEP_RETRIES || !isTransient(err)) throw err;
          return attempt();
        });
      });
    }
    return attempt();
  }

  // Adds retry only on transient failures: a hard rejection (sold out,
  // invalid variant) must surface, and replaying an answered request
  // could double-add.
  function addLineReliably(variantId, quantity, properties) {
    var attempts = 0;
    function attempt() {
      attempts += 1;
      return addLine(variantId, quantity, properties).catch(function (err) {
        console.warn('[EMZ Gift Wrap] add attempt ' + attempts + ' failed', variantId, err);
        if (attempts >= STEP_RETRIES || !isTransient(err)) throw err;
        return delay(350 * attempts).then(attempt);
      });
    }
    return attempt();
  }

  // Builds the mutation queue in three strict stages. Reductions and
  // removals (stage 1 and 2) all run before any add (stage 3): adds can
  // auto-merge into surviving lines, and a reduction computed from the
  // pre-save snapshot would wipe out a merged-in quantity if it ran
  // after the merge.
  function buildQueue() {
    var reductions = [];
    var additions = [];
    var absorbed = {};

    // A plain line this save otherwise leaves untouched, holding the
    // same variant and the same normalized properties, that a
    // re-added plain quantity can be folded into.
    function findPlainSibling(line, baseProps) {
      for (var i = 0; i < lines.length; i++) {
        var other = lines[i];
        if (other.key === line.key || absorbed[other.key]) continue;
        if (other.variantId !== line.variantId || !other.eligible) continue;
        var oa = assignments[other.key];
        if (!oa || oa.pkg !== 0 || other.currentPackage !== 0) continue;
        if (propsEqual(propsWithout(other.properties, PROP_MEMBER), baseProps)) return other;
      }
      return null;
    }

    wrapLines.forEach(function (wrap) {
      reductions.push(function () { return changeLineReliably(wrap.key, 0); });
    });

    lines.forEach(function (line) {
      if (!line.eligible) return;
      var a = assignments[line.key];
      var pkg = a.pkg;
      var qty = pkg > 0 ? Math.max(1, Math.min(a.qty, line.quantity)) : 0;

      var unchanged =
        (pkg === 0 && line.currentPackage === 0) ||
        (pkg === line.currentPackage && qty === line.quantity);
      if (unchanged) return;

      var baseProps = propsWithout(line.properties, PROP_MEMBER);

      if (line.currentPackage === 0 && pkg > 0 && qty < line.quantity) {
        // Partial wrap of a plain line: keep the original at the
        // reduced quantity, add the wrapped remainder as a new line.
        reductions.push(function () { return changeLineReliably(line.key, line.quantity - qty); });
      } else {
        reductions.push(function () { return changeLineReliably(line.key, 0); });
        if (pkg === 0 || qty < line.quantity) {
          var plainQty = pkg === 0 ? line.quantity : line.quantity - qty;
          var sibling = findPlainSibling(line, baseProps);
          if (sibling) {
            absorbed[sibling.key] = true;
            plainQty += sibling.quantity;
            reductions.push(function () { return changeLineReliably(sibling.key, 0); });
          }
          additions.push(function () { return addLineReliably(line.variantId, plainQty, baseProps); });
        }
      }

      if (pkg > 0) {
        var wrappedProps = propsWithout(line.properties, PROP_MEMBER);
        wrappedProps[PROP_MEMBER] = String(pkg);
        additions.push(function () { return addLineReliably(line.variantId, qty, wrappedProps); });
      }
    });

    // Ropes follow their bar: rewrite any rope group whose lines do
    // not already match the plan (hard strip, remove and re-add,
    // both properties carried). Groups already in place stage no
    // mutations, which keeps no-op saves and heal convergence clean.
    ropeGroups().forEach(function (group) {
      var desired = ropePlanForGroup(group);
      if (ropeStateMatchesPlan(group, desired)) return;
      group.lines.forEach(function (line) {
        reductions.push(function () { return changeLineReliably(line.key, 0); });
      });
      Object.keys(desired).forEach(function (pkgKey) {
        var props = {};
        props[PROP_ROPE_FOR] = group.ropeFor;
        if (pkgKey !== '0') props[PROP_MEMBER] = pkgKey;
        additions.push(function () { return addLineReliably(group.variantId, desired[pkgKey], props); });
      });
    });

    packageNumbersInUse().forEach(function (n) {
      additions.push(function () {
        var props = {};
        props[PROP_PACKAGE] = String(n);
        props[PROP_CONTAINS] = containsSummary(n);
        var message = (messagesByPackage[n] || '').trim().slice(0, GIFT_MESSAGE_MAX_CHARS);
        props[PROP_MESSAGE] = message || 'None';
        return addLineReliably(WRAP_VARIANT_PAPER, 1, props);
      });
    });

    return reductions.concat(additions);
  }

  function runQueue(queue) {
    return queue.reduce(function (chain, step) {
      return chain.then(step);
    }, Promise.resolve());
  }

  function save() {
    if (saving) return;
    saving = true;
    clearError();
    saveBtn.classList.add('btn--loading');
    saveBtn.disabled = true;

    // Rebuild the model from the live cart at save time, carrying the
    // customer's package choices over by line key (keys hash variant +
    // properties, so quantity edits keep them stable). The open-time
    // snapshot goes stale while the builder sits open — drawer edits,
    // other tabs, app scripts — and a stale key fails /cart/change.js
    // with a 400 mid-queue, stranding a half-rewritten cart.
    var chosen = assignments;
    fetchCart()
      .then(function (cart) {
        buildModel(cart);
        Object.keys(chosen).forEach(function (key) {
          if (assignments[key]) {
            assignments[key].pkg = chosen[key].pkg;
            assignments[key].qty = chosen[key].qty;
          }
        });
        return runQueue(buildQueue());
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
        // Drop the #gift-wrap hash so the reload lands on a closed
        // builder with the updated cart lines in view.
        if (window.history && history.replaceState) {
          history.replaceState(null, '', window.location.pathname + window.location.search);
        }
        window.scrollTo(0, 0);
        window.location.reload();
      })
      .catch(function (err) {
        console.warn('[EMZ Gift Wrap] Save failed', err);
        saving = false;
        saveBtn.classList.remove('btn--loading');
        saveBtn.disabled = false;
        // Re-render from the real cart so the builder reflects whatever
        // part of the queue landed before the failure; a second Save
        // rebuilds intent from that live state and completes the job.
        openBuilder();
        showError(MSG_SAVE_FAILED);
        errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
  }

  // --------------------------------------------------------- self-healing

  // Inspects the cart for damage left by native cart controls and,
  // when found, rewrites it through the same queue the builder uses:
  // orphaned members get the hard-strip fold, empty or malformed wrap
  // lines are removed and never re-added, and every surviving wrap
  // line comes back with a freshly computed "Contains". Returns false
  // when the cart is already consistent.
  function heal(cart) {
    buildModel(cart);

    var wrapByPkg = {};
    wrapLines.forEach(function (wrap) {
      var n = parseInt((wrap.properties || {})[PROP_PACKAGE], 10) || 0;
      if (!wrapByPkg[n]) wrapByPkg[n] = [];
      wrapByPkg[n].push(wrap);
    });

    var needsHeal = false;

    // Members whose wrap line is gone lose their assignment; the
    // queue then strips the property and folds the quantity back
    // into a matching plain line.
    lines.forEach(function (line) {
      var a = assignments[line.key];
      if (a && a.pkg > 0 && !wrapByPkg[a.pkg]) {
        a.pkg = 0;
        needsHeal = true;
      }
    });

    // Rope followers out of sync with the bar-derived plan trigger
    // the same rewrite: an orphaned "Gift package" on a rope after a
    // wrap-line removal, claims drifted by native quantity edits, or
    // rope units exceeding their bar's units (trimmed), including a
    // bar removed entirely (empty plan removes the whole group).
    // Checked after the bar zeroing above so the plan reflects the
    // healed assignments.
    var ropesRemovedWithBar = false;
    ropeGroups().forEach(function (group) {
      var plan = ropePlanForGroup(group);
      if (ropeStateMatchesPlan(group, plan)) return;
      needsHeal = true;
      var planned = 0;
      Object.keys(plan).forEach(function (k) { planned += plan[k]; });
      if (planned === 0) ropesRemovedWithBar = true;
    });

    var emptied = [];
    var usedMap = {};
    packageNumbersInUse().forEach(function (n) { usedMap[n] = true; });
    Object.keys(wrapByPkg).forEach(function (k) {
      var n = parseInt(k, 10);
      var group = wrapByPkg[k];
      if (!n || !usedMap[n]) {
        // No package number at all, or no member items left.
        emptied.push(n);
        needsHeal = true;
        return;
      }
      if (group.length > 1) {
        // Duplicate wrap lines for one package; the rewrite collapses
        // them to a single line.
        needsHeal = true;
        return;
      }
      var stored = (group[0].properties || {})[PROP_CONTAINS] || '';
      if (stored !== containsSummary(n)) needsHeal = true;
    });

    if (!needsHeal) return Promise.resolve(false);

    var notices = [];
    var numbers = emptied.filter(function (n) { return n > 0; }).sort();
    if (numbers.length === 1) {
      notices.push('Package ' + numbers[0] + ' was empty, so we removed its gift wrap charge.');
    } else if (numbers.length > 1) {
      notices.push('Packages ' + numbers.join(' and ') + ' were empty, so we removed their gift wrap charges.');
    } else if (emptied.length) {
      notices.push('A gift wrap charge without a package was removed.');
    }
    if (ropesRemovedWithBar) {
      notices.push('We removed the rope add-on because its soap bar was removed.');
    }

    return runQueue(buildQueue()).then(function () {
      return { notice: notices.length ? notices.join(' ') : null };
    });
  }

  function runHeal() {
    if (saving || healing) return;
    if (builder && !builder.hidden) return;
    healing = true;
    fetchCart()
      .then(function (cart) { return heal(cart); })
      .then(function (result) {
        if (result === false) {
          healing = false;
          return;
        }
        if (result.notice) {
          try { sessionStorage.setItem(HEAL_NOTICE_FLAG, result.notice); } catch (err) { }
        }
        // One added-event for the header count and drawer rebuild.
        document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
        if (root) {
          // On /cart the page body shows the lines server-rendered,
          // so reload; healing stays true so nothing re-enters first.
          window.location.reload();
          return;
        }
        // Off /cart (drawer edits on any page) the rebuilt drawer is
        // the only cart UI; no reload needed. The notice waits in
        // sessionStorage for the next /cart render.
        healing = false;
      })
      .catch(function (err) {
        healing = false;
        console.warn('[EMZ Gift Wrap] Self-heal failed', err);
      });
  }

  // Debounced: cart:updated can fire several times for one gesture,
  // and healing must never race the builder or its save queue.
  function scheduleHeal() {
    if (saving || healing) return;
    if (builder && !builder.hidden) return;
    clearTimeout(healTimer);
    healTimer = setTimeout(runHeal, 600);
  }

  // --------------------------------------------------------------- events

  function onLinesChange(evt) {
    var select = evt.target.closest('[data-gw-select]');
    var qtyInput = evt.target.closest('[data-gw-qty]');
    var row = evt.target.closest('[data-gw-line]');
    if (!row) return;
    var key = row.getAttribute('data-key');
    var a = assignments[key];
    if (!a) return;

    var line = null;
    for (var i = 0; i < lines.length; i++) {
      if (lines[i].key === key) { line = lines[i]; break; }
    }
    if (!line) return;

    clearError();

    if (select) {
      var value = select.value;
      if (value === 'new') {
        var next = nextFreePackageNumber();
        if (!next) {
          select.value = String(a.pkg);
          showError(MSG_MAX_PACKAGES);
          return;
        }
        a.pkg = next;
      } else {
        a.pkg = parseInt(value, 10) || 0;
      }
      a.qty = line.quantity;
      render();
    } else if (qtyInput) {
      var qty = parseInt(qtyInput.value, 10);
      if (isNaN(qty)) qty = 1;
      a.qty = Math.max(1, Math.min(qty, line.quantity));
      render();
    }
  }

  function onMessageInput(evt) {
    var area = evt.target.closest('[data-gw-message]');
    if (!area) return;
    messagesByPackage[parseInt(area.getAttribute('data-package'), 10)] = area.value;
  }

  // Discoverability pulse: the first time the drawer opens with a
  // wrappable line in the cart, the entry row runs one subtle
  // attention animation. Once per session via sessionStorage, with an
  // in-page fallback flag so blocked storage (private browsing) still
  // caps it at one per page view. Reduced-motion users never pulse
  // (belt; the CSS guard is the suspenders). Off /cart the tag-based
  // exclusions have no data (tags come from the builder snippet), so
  // a tag-excluded line can trigger the pulse; accepted for an
  // attention heuristic. The row survives drawer rebuilds (it sits
  // outside [data-products]), so the class must be removed on
  // animationend rather than left behind.
  function maybePulseDrawerRow() {
    if (pulseShownThisPage) return;
    var row = document.querySelector('.emz-gift-wrap-drawer-row');
    if (!row || row.classList.contains(PULSE_CLASS)) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    try {
      if (sessionStorage.getItem(PULSE_FLAG)) return;
    } catch (err) { }
    fetchCart().then(function (cart) {
      var any = (cart.items || []).some(function (item) {
        return eligibility(item).ok;
      });
      if (!any || pulseShownThisPage) return;
      pulseShownThisPage = true;
      try { sessionStorage.setItem(PULSE_FLAG, '1'); } catch (err) { }
      row.classList.add(PULSE_CLASS);
      row.addEventListener('animationend', function () {
        row.classList.remove(PULSE_CLASS);
      }, { once: true });
    }).catch(function () { });
  }

  function init() {
    root = document.querySelector('[data-gift-wrap]');

    // Self-healing triggers wire on EVERY page this script loads on
    // (the drawer entry snippet loads it sitewide): drawer edits made
    // anywhere must heal immediately, not wait for a /cart visit.
    // ajaxProduct:added is the belt for add flows; a consistent cart
    // makes the extra debounced check a no-op.
    document.addEventListener('cart:updated', scheduleHeal);
    document.addEventListener('ajaxProduct:added', scheduleHeal);
    scheduleHeal();

    // theme.js dispatches drawerOpen.CartDrawer on every cart drawer
    // open, manual and post-add alike.
    document.addEventListener('drawerOpen.CartDrawer', maybePulseDrawerRow);

    // A parked heal notice normally shows inside the builder, but a
    // heal can empty the cart entirely (bar removed -> ropes removed
    // -> package empty -> wrap charge removed), and /cart then
    // renders without the builder. Surface the notice standalone so
    // the disappearing charges are explained; the sitewide drawer
    // snippet already loads the stylesheet.
    if (!root && document.body && document.body.classList.contains('template-cart')) {
      var parked = null;
      try {
        parked = sessionStorage.getItem(HEAL_NOTICE_FLAG);
        if (parked) sessionStorage.removeItem(HEAL_NOTICE_FLAG);
      } catch (err) { }
      if (parked) {
        var holder = document.querySelector('.page-content .section-header') ||
          document.querySelector('.page-content');
        if (holder) {
          var standalone = document.createElement('p');
          standalone.className = 'emz-gift-wrap__notice';
          standalone.setAttribute('role', 'status');
          standalone.textContent = parked;
          holder.appendChild(standalone);
        }
      }
    }

    // Everything below is builder wiring; it exists only on /cart.
    if (!root) return;

    builder = root.querySelector('[data-gw-builder]');
    toggle = root.querySelector('[data-gw-toggle]');
    linesEl = root.querySelector('[data-gw-lines]');
    packagesEl = root.querySelector('[data-gw-packages]');
    errorEl = root.querySelector('[data-gw-error]');
    noticeEl = root.querySelector('[data-gw-notice]');
    saveBtn = root.querySelector('[data-gw-save]');
    cancelBtn = root.querySelector('[data-gw-cancel]');
    wrapPrice = root.getAttribute('data-wrap-price') || '';

    // A heal that removed an empty wrap line reloads the page; its
    // notice is parked in sessionStorage and shown here afterward.
    if (noticeEl) {
      var healNote = null;
      try {
        healNote = sessionStorage.getItem(HEAL_NOTICE_FLAG);
        if (healNote) sessionStorage.removeItem(HEAL_NOTICE_FLAG);
      } catch (err) { }
      if (healNote) {
        noticeEl.textContent = healNote;
        noticeEl.hidden = false;
      }
    }

    var metaEl = root.querySelector('[data-gw-product-meta]');
    if (metaEl) {
      try {
        JSON.parse(metaEl.textContent).forEach(function (entry) {
          tagsByProduct[entry.id] = entry.tags || [];
        });
      } catch (err) {
        console.warn('[EMZ Gift Wrap] Could not parse product meta', err);
      }
    }

    toggle.addEventListener('click', function () {
      if (builder.hidden) openBuilder();
      else closeBuilder();
    });
    cancelBtn.addEventListener('click', closeBuilder);
    saveBtn.addEventListener('click', save);
    linesEl.addEventListener('change', onLinesChange);
    packagesEl.addEventListener('input', onMessageInput);

    // ---- landing scroll ------------------------------------------
    // A single scrollIntoView lands wrong on Safari: the header can
    // cover the builder, and layout keeps shifting after
    // DOMContentLoaded (cart images, the free-shipping banner, app
    // embeds), while iOS collapses its URL bar even on programmatic
    // scrolls. Each assert measures what is pinned over the viewport
    // top AT THAT MOMENT and corrects the residual, because the
    // pinned stack changes height as the page scrolls (measured on
    // the cart page: 175px at page top compacting to 100px once
    // scrolled). The anchor is the [data-gift-wrap] container, whose
    // top edge is the entry button. The customer scrolling on their
    // own cancels everything.
    //
    // What actually pins is defined by emz-sticky-header-fix.css: the
    // SECTION wrappers, announcement at top 0 (z-60) and the header
    // section below it (z-50), both position sticky at all times.
    // .header-sticky-wrapper itself is position relative and
    // .site-header sits static inside it, so measuring those reads
    // zero overlay; they stay in the list only as fallbacks for
    // other header configurations, gated by the position check.

    var OVERLAY_SELECTORS = '.shopify-section-group-header-group, [id$="__announcement"], [id$="__header"], .emz-announcement-section, .emz-header-section, .site-header, #HeaderWrapper';
    var LANDING_GAP = 16;

    // Depth of the pinned header stack right now. Only fixed or
    // sticky elements count: in-flow header content scrolls away
    // with the page and never overlays the builder.
    function overlayDepth() {
      var depth = 0;
      var els = document.querySelectorAll(OVERLAY_SELECTORS);
      for (var i = 0; i < els.length; i++) {
        var style = getComputedStyle(els[i]);
        if (style.position !== 'fixed' && style.position !== 'sticky') continue;
        var rect = els[i].getBoundingClientRect();
        if (rect.height === 0 || rect.top > innerHeight / 2) continue;
        if (rect.bottom > depth) depth = rect.bottom;
      }
      return Math.max(0, depth);
    }

    // Minimal-scroll landing: the builder must be visible, but the
    // page should still read as the cart. The builder top may rest
    // anywhere between the pinned stack (plus a gap) and just past
    // mid-viewport; inside that band nothing scrolls, so the page
    // title, the continue-shopping line, and on taller screens the
    // first cart lines stay in view. Scrolling engages only when the
    // builder is covered by the header (scroll up to the gap) or
    // below the band (scroll down just far enough).
    function landingDelta() {
      var top = root.getBoundingClientRect().top;
      var floor = overlayDepth() + LANDING_GAP;
      var ceiling = Math.max(floor + 40, Math.round(window.innerHeight * 0.55));
      if (top < floor - 4) return top - floor;
      if (top > ceiling + 4) return top - ceiling;
      return 0;
    }

    function builderVisiblyPlaced() {
      return landingDelta() === 0;
    }

    var programmaticScroll = false;

    function assertScroll() {
      var delta = landingDelta();
      if (delta === 0) return;
      // Instant, overriding the theme's scroll-behavior: smooth CSS.
      // A plain scroll still animates under that CSS, and animated
      // scrolls need animation frames that throttled or interrupted
      // pages never grant, so they can silently go nowhere. Safari's
      // stop-wherever-it-is smooth scrolling is how the builder
      // landed mid-cart in the first place.
      var htmlStyle = document.documentElement.style;
      var previous = htmlStyle.scrollBehavior;
      htmlStyle.scrollBehavior = 'auto';
      programmaticScroll = true;
      window.scrollBy(0, delta);
      htmlStyle.scrollBehavior = previous;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { programmaticScroll = false; });
      });
    }

    function landBuilderInView() {
      var deadline = Date.now() + 3200;
      var cancelled = false;
      function cancel() { cancelled = true; }
      window.addEventListener('wheel', cancel, { once: true, passive: true });
      window.addEventListener('touchstart', cancel, { once: true, passive: true });
      window.addEventListener('keydown', cancel, { once: true });
      // A scroll this code did not make is the customer taking over,
      // whatever input produced it: scrollbar drags fire only scroll
      // events, never wheel or touch.
      function onScroll() {
        if (!programmaticScroll) cancel();
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      setTimeout(function () { window.removeEventListener('scroll', onScroll); }, 3300);

      assertScroll();

      function reassert() {
        if (cancelled || Date.now() > deadline) return;
        if (!builderVisiblyPlaced()) assertScroll();
      }
      setTimeout(reassert, 450);
      setTimeout(reassert, 1100);
      setTimeout(reassert, 2200);
      if (document.readyState !== 'complete') {
        window.addEventListener('load', function () { setTimeout(reassert, 60); }, { once: true });
      }
      // iOS URL bar collapse resizes the visual viewport; re-check
      // once each resize settles, inside the same deadline window.
      if (window.visualViewport) {
        var vv = window.visualViewport;
        var onResize = function () { setTimeout(reassert, 120); };
        vv.addEventListener('resize', onResize);
        setTimeout(function () { vv.removeEventListener('resize', onResize); }, 3400);
      }
    }

    function openFromHash() {
      if (window.location.hash !== OPEN_HASH) return;
      document.dispatchEvent(new CustomEvent('cart:close'));
      openBuilder();
      landBuilderInView();
    }

    window.addEventListener('hashchange', openFromHash);
    openFromHash();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
